import { useCallback, useEffect, useState } from 'react';

/**
 * This device's own notification preferences — quiet hours, sounds, volume.
 *
 * ── Why these are not clinic settings ───────────────────────────────
 * They used to be. `notificationSettings.quietHours` and `.soundSettings` are
 * clinic-level columns, read by `NotificationContext` in the browser of
 * *every* logged-in user. So one owner setting 22:00–08:00 muted the night
 * receptionist's patient-called ring, and one person muting the payment beep
 * muted it for the whole clinic. A sleep schedule is not a clinic policy.
 *
 * The correct fix is a `user_notification_preferences` table. That is a
 * backend change; until it lands these live in `localStorage`, keyed per user
 * and clinic, seeded once from whatever the clinic has stored so
 * nobody's existing configuration is lost on first load. When the table
 * exists, only this hook changes — no caller does.
 *
 * The tradeoff is honest and stated in the UI: preferences set here apply to
 * this browser only.
 */

export interface QuietHours {
  enabled: boolean;
  /** `HH:mm`, 24-hour, in the clinic's timezone. */
  from: string;
  to: string;
  /**
   * Days the window applies to, `0`=Sunday … `6`=Saturday. Empty means every
   * day — the previous behaviour, and the sane default.
   */
  days: number[];
  /**
   * Let the events a clinic cannot afford to miss ring anyway.
   *
   * Quiet hours previously suppressed everything, which is right for a
   * low-stock warning and wrong for a patient physically waiting at the chair.
   * `patient_called` and `receptionist_called` are the two that are always
   * someone standing there needing a response.
   */
  allowUrgent: boolean;
}

export interface SoundPrefs {
  patientCalledSound: boolean;
  paymentPendingSound: boolean;
  generalNotificationSound: boolean;
  /** Master mute. Wins over the three per-event toggles. */
  muted: boolean;
  /** 0–1. Applied to the Web Audio gain node. */
  volume: number;
}

export interface MyNotificationPrefs {
  quietHours: QuietHours;
  sounds: SoundPrefs;
}

export const DEFAULT_PREFS: MyNotificationPrefs = {
  quietHours: {
    enabled: false,
    from: '22:00',
    to: '08:00',
    days: [],
    allowUrgent: true,
  },
  sounds: {
    patientCalledSound: true,
    paymentPendingSound: true,
    generalNotificationSound: true,
    muted: false,
    volume: 0.6,
  },
};

/** Per user AND per clinic: one person may work at two clinics with
 *  different hours, and the two must not overwrite each other. */
function storageKey(userId?: string, clinicId?: string) {
  return `dc:notif-prefs:${clinicId ?? 'no-clinic'}:${userId ?? 'anon'}`;
}

/** Legacy clinic values, used only to seed a device that has none. */
export interface PrefsSeed {
  quietHours?: { enabled: boolean; from: string; to: string };
  soundSettings?: {
    patientCalledSound: boolean;
    paymentPendingSound: boolean;
    generalNotificationSound: boolean;
  };
}

function merge(stored: unknown, seed?: PrefsSeed): MyNotificationPrefs {
  const s = (stored ?? {}) as Partial<MyNotificationPrefs>;

  return {
    quietHours: {
      ...DEFAULT_PREFS.quietHours,
      ...(seed?.quietHours ?? {}),
      ...(s.quietHours ?? {}),
      // `days` and `allowUrgent` have no legacy counterpart, so a seeded
      // device must still get the defaults for them rather than `undefined`.
      days: s.quietHours?.days ?? DEFAULT_PREFS.quietHours.days,
      allowUrgent:
        s.quietHours?.allowUrgent ?? DEFAULT_PREFS.quietHours.allowUrgent,
    },
    sounds: {
      ...DEFAULT_PREFS.sounds,
      ...(seed?.soundSettings ?? {}),
      ...(s.sounds ?? {}),
    },
  };
}

function read(key: string, seed?: PrefsSeed): MyNotificationPrefs {
  try {
    const raw = localStorage.getItem(key);
    return merge(raw ? JSON.parse(raw) : null, seed);
  } catch {
    // Private mode, blocked storage, or corrupt JSON. Defaults are always a
    // usable configuration, so this never blocks the page.
    return merge(null, seed);
  }
}

export function useMyNotificationPrefs(
  userId?: string,
  clinicId?: string,
  seed?: PrefsSeed,
) {
  const key = storageKey(userId, clinicId);
  const [prefs, setPrefs] = useState<MyNotificationPrefs>(() =>
    read(key, seed),
  );

  // Re-read when the identity changes — switching clinic must not carry
  // the previous clinic's quiet hours across.
  useEffect(() => {
    setPrefs(read(key, seed));
    // `seed` is a fresh object every render; the identity keys are what
    // actually decide which record to load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  /** Applies a change and persists it. A failed write is not surfaced: the
   *  value still applies for this session, and there is nothing the user
   *  could do about blocked storage anyway. */
  const persist = useCallback(
    (produce: (prev: MyNotificationPrefs) => MyNotificationPrefs) =>
      setPrefs((prev) => {
        const next = produce(prev);
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* blocked or full storage — session-only is an acceptable fallback */
        }
        return next;
      }),
    [key],
  );

  const setQuietHours = useCallback(
    (patch: Partial<QuietHours>) =>
      persist((prev) => ({
        ...prev,
        quietHours: { ...prev.quietHours, ...patch },
      })),
    [persist],
  );

  const setSounds = useCallback(
    (patch: Partial<SoundPrefs>) =>
      persist((prev) => ({ ...prev, sounds: { ...prev.sounds, ...patch } })),
    [persist],
  );

  return { prefs, setQuietHours, setSounds };
}

/** Notification types that ignore quiet hours when `allowUrgent` is set. */
export const URGENT_TYPES = new Set(['patient_called', 'receptionist_called']);

/**
 * Whether `now` falls inside the quiet window.
 *
 * Handles the overnight case (22:00→08:00) by testing the union of two ranges
 * rather than a single comparison. `days`, when non-empty, is matched against
 * the day the window *starts* on, so a Friday-night window still applies at
 * 01:00 Saturday.
 */
export function isQuietAt(q: QuietHours, now: Date): boolean {
  if (!q.enabled) return false;

  const minutes = now.getHours() * 60 + now.getMinutes();
  const [fh, fm] = q.from.split(':').map(Number);
  const [th, tm] = q.to.split(':').map(Number);
  const from = fh * 60 + fm;
  const to = th * 60 + tm;

  const overnight = from > to;
  const inWindow = overnight
    ? minutes >= from || minutes < to
    : minutes >= from && minutes < to;

  if (!inWindow) return false;
  if (q.days.length === 0) return true;

  // For an overnight window, the hours after midnight belong to the previous
  // day's selection.
  const startDay =
    overnight && minutes < to ? (now.getDay() + 6) % 7 : now.getDay();

  return q.days.includes(startDay);
}
